import { Reveal } from "./Reveal";

/*
  The Enterprise AI Gap — industry-challenge narrative.
  Desktop/tablet (md+): the self-contained, auto-scaling 16:9 embed in an isolated iframe.
  Mobile (<md): a native, vertically-stacked version so the text stays readable on phones.
*/

const stages = [
  {
    n: "01",
    label: "Pre-Agentic · the lowland",
    labelColor: "#8F7460",
    head: "Humans did the thinking.",
    points: [
      "Applications executed predefined logic",
      "Work spanned systems, teams and data silos",
      "Intelligence required specialized teams and time",
    ],
  },
  {
    n: "02",
    label: "Agentic Promise · base camp",
    labelColor: "#D1633D",
    head: "AI learned to think, reason & act.",
    points: [
      "LLMs made reasoning and automation accessible",
      "Agents and copilots could be built in weeks",
      "Enterprise experimentation exploded",
    ],
  },
  {
    n: "03",
    label: "Production · summit",
    labelColor: "#D1633D",
    head: "Now the challenge is production.",
    points: [
      "Agents need governed enterprise context",
      "Accuracy, reliability and cost get tested at scale",
      "Days to prove; months to operationalize",
    ],
    summit: true,
  },
];

// The ascent, top (summit) → bottom (base camp).
const route = [
  { type: "zone", label: "Summit · Production" },
  { type: "point", t: "Time-to-Value", strong: true },
  { type: "point", t: "Cost at Scale" },
  { type: "point", t: "Observability & Governance" },
  { type: "zone", label: "Death zone — where pilots stall" },
  { type: "point", t: "Engineering Tax" },
  { type: "point", t: "Accuracy & Trust" },
  { type: "point", t: "Context & Data" },
  { type: "zone", label: "Base camp — graveyard of PoCs" },
];

const StageCard = ({ s }) => (
  <div
    data-testid={`ai-gap-stage-${s.n}`}
    className={`rounded-2xl border p-5 ${
      s.summit ? "border-[#3E2818]/15 bg-[#211307]" : "border-[#3E2818]/10 bg-white"
    }`}
  >
    <span
      className="font-mono text-[11px] tracking-[0.14em] uppercase"
      style={{ color: s.summit ? "#E88B6B" : s.labelColor }}
    >
      {s.n} — {s.label}
    </span>
    <h3 className={`mt-2 text-xl leading-snug ${s.summit ? "text-white" : "text-[#211307]"}`}>
      {s.head}
    </h3>
    <ul className="mt-3 space-y-1.5">
      {s.points.map((p) => (
        <li
          key={p}
          className={`flex gap-2.5 text-sm leading-relaxed ${
            s.summit ? "text-[#D8CBBD]" : "text-[#5C4A3E]"
          }`}
        >
          <span className="mt-[8px] h-1 w-1 shrink-0 rounded-full bg-[#D1633D]" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ClimbRoute = () => (
  <div className="relative mt-5">
    <span
      aria-hidden
      className="absolute left-3 top-1 bottom-1 w-px border-l border-dashed border-[#D1633D]/50"
    />
    <ul className="space-y-3.5">
      {route.map((r, i) =>
        r.type === "zone" ? (
          <li key={i} className="pl-7">
            <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#D1633D]/80">
              {r.label}
            </span>
          </li>
        ) : (
          <li key={i} className="relative pl-7">
            <span className="absolute left-3 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D1633D] ring-4 ring-[#F4EFE6]" />
            <span className={`text-sm ${r.strong ? "font-semibold text-[#211307]" : "text-[#4A3B2E]"}`}>
              {r.t}
            </span>
          </li>
        )
      )}
    </ul>
  </div>
);

export const EnterpriseAIGap = () => (
  <section id="enterprise-ai-gap" className="relative z-[91] bg-[#EDE7DC] pt-4 pb-12 lg:pb-16" data-testid="enterprise-ai-gap-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      {/* Desktop / tablet: the auto-scaling infographic */}
      <Reveal>
        <iframe
          src="/embeds/enterprise-ai-gap.html"
          title="The Enterprise AI Gap"
          loading="lazy"
          scrolling="no"
          data-testid="enterprise-ai-gap-embed"
          className="hidden md:block w-full border-0 pointer-events-none"
          style={{ aspectRatio: "16 / 9" }}
        />
      </Reveal>

      {/* Mobile: stacked, readable version */}
      <div className="md:hidden pt-2" data-testid="enterprise-ai-gap-mobile">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#D1633D]">
            The Enterprise AI Gap
          </p>
          <h2 className="mt-3 text-3xl leading-[1.12] tracking-tight text-[#211307]">
            The Gap Isn&rsquo;t Building <span className="text-[#D1633D]">PoCs</span>.
            <br />
            It&rsquo;s Delivering <span className="text-[#D1633D]">Value</span> in{" "}
            <span className="text-[#D1633D]">Production</span>.
          </h2>
          <p className="mt-3 text-sm text-[#5C4A3E]">
            Everyone reaches base camp. Only few make it to summit.
          </p>
        </Reveal>

        <div className="mt-8 space-y-4">
          {stages.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <StageCard s={s} />
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-8 rounded-2xl border border-[#3E2818]/10 bg-[#F4EFE6] p-5">
            <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#8F7460]">
              The climb to value
            </p>
            <p className="mt-1.5 text-sm text-[#5C4A3E]">
              The checkpoints every agent must pass to reach production.
            </p>
            <ClimbRoute />
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-6 rounded-2xl bg-[#211307] p-6">
            <p className="text-base italic leading-relaxed text-[#EFE4D6]">
              The question has shifted — from{" "}
              <span className="not-italic text-[#BBA591]">&ldquo;Can AI do this?&rdquo;</span> to{" "}
              <span className="text-white">
                &ldquo;Can we run it in production, at enterprise scale?&rdquo;
              </span>
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
